import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useAuth } from "@/features/auth/hooks/useAuthentication";
import { ChatProvider } from "@/features/chat/context/ChatContext";
import { cn } from "@/shared/lib/utils";
import { Sidebar, Topbar } from "@/widgets/navigation";

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const auth = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Handle redirect when not authenticated
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      const currentOrigin = window.location.origin;
      window.location.href = `${import.meta.env.VITE_CORE_API_URL}/auth/login?origin=${encodeURIComponent(currentOrigin)}`;
    }
  }, [auth.isLoading, auth.isAuthenticated]);

  // Open sidebar by default on larger screens
  useEffect(() => {
    if (window.innerWidth >= 768) {
      setSidebarOpen(true);
    }
  }, []);

  // Show loading state while authentication is being checked
  if (auth.isLoading) {
    return (
      <div className="flex h-screen bg-background items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show redirecting message
  if (!auth.isAuthenticated) {
    return (
      <div className="flex h-screen bg-background items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Handle other authentication errors
  if (auth.error) {
    return (
      <div className="flex h-screen bg-background items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">Authentication error: {auth.error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <ChatProvider>
      <div className="flex h-screen bg-background">
        {/* Mobile overlay backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onOpen={() => setSidebarOpen(true)}
        />

        {/* Main content area */}
        <div className={cn("flex-1 flex flex-col overflow-hidden md:min-w-[390px]")}>
          {/* Topbar */}
          <Topbar onMenuClick={() => setSidebarOpen(true)} sidebarOpen={sidebarOpen} />

          {/* Page content */}
          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-background">
            <Outlet />
          </main>
        </div>
      </div>
    </ChatProvider>
  );
}
